import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ban, KeyRound, PlusCircle, RefreshCcw, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { ConfirmDialog, FormField, StatusBadge } from '../components/ui';
import { useSetStoreAdminStatus } from '../features/franchise-admin/hooks';
import { apiRequest } from '../lib/api';
import type { AdminNavItem, AdminSurface, AdminSurfaceConfig } from '../surface';
import {
  USER_PASSWORD_RESET_CONFIRMATION,
  validateCreateAdminUserInput,
  validateUserPasswordResetInput,
  type CreateAdminUserInput,
  type ResetUserPasswordInput,
} from '../features/system-admin/data';

type ScopedRow = Record<string, unknown> & { id: string };

interface ScopedStoreOption {
  franchise?: { name?: string } | null;
  franchise_id: string | null;
  id: string;
  name: string;
}

interface ColumnConfig {
  key: string;
  label: string;
}

interface PageConfig {
  columns: ColumnConfig[];
  emptyLabel: string;
  select: string;
  table: string;
  filters?: Array<[string, string]>;
  orderBy?: string;
}

interface ScopedDataPageProps {
  item: AdminNavItem;
  surfaceConfig: AdminSurfaceConfig;
}

const pageConfigs: Record<Exclude<AdminSurface, 'system_admin'>, Record<string, PageConfig>> = {
  franchise_admin: {
    '/users': {
      table: 'users',
      select: 'id, username, nickname, staff_phone_number, status, created_at, store:stores(name)',
      filters: [['role', 'staff_admin']],
      columns: [
        { key: 'username', label: '계정 ID' },
        { key: 'nickname', label: '이름' },
        { key: 'store', label: '매장' },
        { key: 'staff_phone_number', label: '연락처' },
        { key: 'status', label: '상태' },
      ],
      emptyLabel: '조회 가능한 매장 관리자가 없습니다.',
    },
    '/customers': {
      table: 'users',
      select: 'id, internal_login_id, nickname, status, created_at, store:stores(name)',
      filters: [['role', 'customer']],
      columns: [
        { key: 'internal_login_id', label: '계정 ID' },
        { key: 'nickname', label: '닉네임' },
        { key: 'store', label: '매장' },
        { key: 'status', label: '상태' },
      ],
      emptyLabel: '조회 가능한 고객이 없습니다.',
    },
    '/reports': {
      table: 'audit_logs',
      select: 'id, action_type, target_type, created_at',
      columns: [
        { key: 'action_type', label: '작업' },
        { key: 'target_type', label: '대상' },
        { key: 'created_at', label: '일시' },
      ],
      emptyLabel: '조회 가능한 운영 로그가 없습니다.',
    },
  },
  pub_admin: {
    '/buy-in': {
      table: 'buy_in_transactions',
      select: 'id, transaction_type, amount, customer_id, created_at',
      columns: [
        { key: 'transaction_type', label: '유형' },
        { key: 'amount', label: '포인트' },
        { key: 'customer_id', label: '고객' },
        { key: 'created_at', label: '일시' },
      ],
      emptyLabel: '최근 Buy-in 내역이 없습니다.',
    },
    '/prize': {
      table: 'prize_transactions',
      select: 'id, amount, customer_id, tournament_id, created_at',
      columns: [
        { key: 'amount', label: '상금' },
        { key: 'customer_id', label: '고객' },
        { key: 'tournament_id', label: '토너먼트' },
        { key: 'created_at', label: '일시' },
      ],
      emptyLabel: '최근 Prize 내역이 없습니다.',
    },
    '/bonus': {
      table: 'bonus_transactions',
      select: 'id, amount, reason, status, created_at',
      columns: [
        { key: 'amount', label: '보너스' },
        { key: 'reason', label: '사유' },
        { key: 'status', label: '상태' },
        { key: 'created_at', label: '일시' },
      ],
      emptyLabel: '최근 Bonus 내역이 없습니다.',
    },
    '/tournaments': {
      table: 'tournaments',
      select: 'id, name, status, start_time, created_at',
      orderBy: 'start_time',
      columns: [
        { key: 'name', label: '토너먼트' },
        { key: 'status', label: '상태' },
        { key: 'start_time', label: '시작' },
      ],
      emptyLabel: '조회 가능한 토너먼트가 없습니다.',
    },
    '/history': {
      table: 'audit_logs',
      select: 'id, action_type, target_type, created_at',
      columns: [
        { key: 'action_type', label: '작업' },
        { key: 'target_type', label: '대상' },
        { key: 'created_at', label: '일시' },
      ],
      emptyLabel: '조회 가능한 운영 이력이 없습니다.',
    },
  },
};

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'number') {
    return value.toLocaleString('ko-KR');
  }

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return new Intl.DateTimeFormat('ko-KR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value));
    }

    return value.length > 18 ? `${value.slice(0, 8)}...` : value;
  }

  if (typeof value === 'object' && 'name' in value) {
    return String((value as { name?: unknown }).name ?? '-');
  }

  return String(value);
}

async function fetchCloudflareScopedRows(surface: AdminSurface, path: string): Promise<ScopedRow[]> {
  const endpoint = surface === 'franchise_admin'
    ? {
        '/users': '/api/v1/franchise/store-admins',
        '/customers': '/api/v1/franchise/customers/summary',
        '/reports': '/api/v1/franchise/reports',
      }[path]
    : surface === 'pub_admin'
      ? {
          '/buy-in': '/api/v1/pub/buy-ins',
          '/prize': '/api/v1/pub/prizes',
          '/bonus': '/api/v1/pub/bonuses',
          '/tournaments': '/api/v1/pub/tournaments',
          '/history': '/api/v1/pub/history',
        }[path]
      : undefined;
  if (!endpoint) throw new Error('Cloudflare API로 전환되지 않은 화면입니다.');
  return apiRequest<{ rows: ScopedRow[] }>(endpoint).then((response) => response.rows);
}

async function fetchCloudflareStoreOptions(): Promise<ScopedStoreOption[]> {
  return apiRequest<{ rows: ScopedStoreOption[] }>('/api/v1/franchise/stores').then((response) => response.rows);
}

function getScopedUserLabel(row: ScopedRow): string {
  const nickname = typeof row.nickname === 'string' && row.nickname.trim() ? row.nickname.trim() : null;
  const username = typeof row.username === 'string' && row.username.trim() ? row.username.trim() : null;
  return nickname ?? username ?? row.id.slice(0, 8);
}

function ScopedCreateStaffAdminDialog({
  onClose,
  onDone,
  stores,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
  stores: ScopedStoreOption[];
}) {
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [staffPhoneNumber, setStaffPhoneNumber] = useState('');
  const [storeId, setStoreId] = useState('');
  const [username, setUsername] = useState('');
  const selectedStore = stores.find((store) => store.id === storeId);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    const input: CreateAdminUserInput = {
      confirmPassword,
      franchiseId: selectedStore?.franchise_id ?? '',
      nickname,
      password,
      reason,
      role: 'staff_admin',
      source: 'franchise_admin_react',
      staffPhoneNumber,
      storeId,
      username,
    };

    const validationError = validateCreateAdminUserInput(input);

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await apiRequest('/api/v1/franchise/store-admins', {
        method: 'POST',
        idempotent: true,
        body: JSON.stringify({
          username: input.username.trim(),
          password: input.password,
          nickname: input.nickname.trim(),
          staff_phone_number: input.staffPhoneNumber.trim() || null,
          store_id: input.storeId,
          reason: input.reason.trim(),
        }),
      });
      onDone('매장 관리자 계정을 생성했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '매장 관리자 계정을 생성하지 못했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-panel" aria-labelledby="scoped-create-admin-title">
        <div className="modal-heading">
          <div>
            <h3 id="scoped-create-admin-title">매장 관리자 추가</h3>
            <p>현재 프랜차이즈에 속한 매장의 관리자 계정을 생성합니다.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </div>
        <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
          {errorMessage ? <div className="action-notice action-notice--danger">{errorMessage}</div> : null}
          <label className="form-field">
            매장
            <select value={storeId} onChange={(event) => setStoreId(event.target.value)} disabled={isSubmitting}>
              <option value="">선택</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.franchise?.name ? `${store.franchise.name} / ` : ''}
                  {store.name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            계정 ID
            <input value={username} autoComplete="username" onChange={(event) => setUsername(event.target.value)} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            이름 또는 표시명
            <input value={nickname} onChange={(event) => setNickname(event.target.value)} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            연락처
            <input value={staffPhoneNumber} inputMode="tel" onChange={(event) => setStaffPhoneNumber(event.target.value)} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            임시 비밀번호
            <input value={password} type="password" autoComplete="new-password" onChange={(event) => setPassword(event.target.value)} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            임시 비밀번호 확인
            <input
              value={confirmPassword}
              type="password"
              autoComplete="new-password"
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={isSubmitting}
            />
          </label>
          <label className="form-field">
            생성 사유
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} disabled={isSubmitting} />
          </label>
          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={isSubmitting}>
              취소
            </button>
            <button className="primary-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? '생성 중' : '생성'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ScopedPasswordResetDialog({
  currentAdminId,
  onClose,
  onDone,
  target,
}: {
  currentAdminId: string;
  onClose: () => void;
  onDone: (message: string) => void;
  target: ScopedRow;
}) {
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [reason, setReason] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    const input: ResetUserPasswordInput = {
      confirmPassword,
      confirmationText,
      currentAdminId,
      newPassword,
      reason,
      source: 'franchise_admin_react',
      userId: target.id,
    };
    const validationError = validateUserPasswordResetInput(input);

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await apiRequest(`/api/v1/franchise/store-admins/${target.id}/reset-password`, {
        method: 'POST',
        idempotent: true,
        body: JSON.stringify({
          new_password: input.newPassword,
          confirmation_text: input.confirmationText.trim(),
          reason: input.reason.trim(),
        }),
      });
      onDone('매장 관리자 비밀번호를 재설정했습니다.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '비밀번호를 재설정하지 못했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-panel" aria-labelledby="scoped-reset-password-title">
        <div className="modal-heading">
          <div>
            <h3 id="scoped-reset-password-title">비밀번호 재설정</h3>
            <p>{getScopedUserLabel(target)} 계정의 임시 비밀번호를 설정합니다.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </div>
        <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
          {errorMessage ? <div className="action-notice action-notice--danger">{errorMessage}</div> : null}
          <label className="form-field">
            임시 비밀번호
            <input value={newPassword} type="password" autoComplete="new-password" onChange={(event) => setNewPassword(event.target.value)} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            임시 비밀번호 확인
            <input
              value={confirmPassword}
              type="password"
              autoComplete="new-password"
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={isSubmitting}
            />
          </label>
          <label className="form-field">
            재설정 사유
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} disabled={isSubmitting} />
          </label>
          <label className="form-field">
            확인 문구
            <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={USER_PASSWORD_RESET_CONFIRMATION} disabled={isSubmitting} />
          </label>
          <p className="form-help">
            확인 문구 <strong>{USER_PASSWORD_RESET_CONFIRMATION}</strong>를 정확히 입력해야 실행됩니다.
          </p>
          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={isSubmitting}>
              취소
            </button>
            <button className="danger-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? '재설정 중' : '재설정'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ScopedStoreAdminStatusDialog({
  onClose,
  onDone,
  target,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
  target: ScopedRow;
}) {
  const mutation = useSetStoreAdminStatus();
  const [reason, setReason] = useState('');
  const suspending = target.status !== 'suspended';
  const label = getScopedUserLabel(target);

  return (
    <ConfirmDialog
      title={suspending ? '매장 관리자 정지' : '매장 관리자 정지 해제'}
      description={
        suspending
          ? '정지하면 해당 계정의 로그인 세션이 모두 끊기고 다시 로그인할 수 없습니다.'
          : '정지를 해제하면 해당 계정이 다시 로그인할 수 있습니다.'
      }
      confirmLabel={suspending ? '정지' : '정지 해제'}
      pendingLabel="처리 중"
      danger={suspending}
      disabled={reason.trim().length < 3}
      onClose={onClose}
      onConfirm={async () => {
        await mutation.mutateAsync({ id: target.id, status: suspending ? 'suspended' : 'active', reason });
        onDone(`${label} 계정을 ${suspending ? '정지' : '정지 해제'}했습니다.`);
      }}
      rows={[
        ['계정', label],
        ['매장', formatCell(target.store)],
      ]}
    >
      <FormField label="사유 (3자 이상)">
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={200} />
      </FormField>
    </ConfirmDialog>
  );
}

export default function ScopedDataPage({ item, surfaceConfig }: ScopedDataPageProps) {
  const { profile } = useAuth();
  const config =
    surfaceConfig.surface === 'system_admin'
      ? null
      : pageConfigs[surfaceConfig.surface]?.[item.path] ?? null;
  const Icon = item.icon;
  const canManageStaffAdmins = surfaceConfig.surface === 'franchise_admin' && item.path === '/users';
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [passwordResetTarget, setPasswordResetTarget] = useState<ScopedRow | null>(null);
  const [statusTarget, setStatusTarget] = useState<ScopedRow | null>(null);

  const query = useQuery({
    enabled: !!config,
    queryKey: ['scoped-data', surfaceConfig.surface, item.path],
    queryFn: () => fetchCloudflareScopedRows(surfaceConfig.surface, item.path),
  });
  const storesQuery = useQuery({
    enabled: canManageStaffAdmins,
    queryKey: ['scoped-data', surfaceConfig.surface, item.path, 'store-options'],
    queryFn: fetchCloudflareStoreOptions,
  });

  if (!config) {
    return (
      <div className="page-stack">
        <section className="page-heading">
          <div>
            <p className="section-eyebrow">{surfaceConfig.shortTitle}</p>
            <h2>{item.label}</h2>
            <p>{item.description}</p>
          </div>
          <Icon size={30} />
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="section-eyebrow">{surfaceConfig.shortTitle}</p>
          <h2>{item.label}</h2>
          <p>{item.description}</p>
        </div>
        <Icon size={30} />
      </section>

      {notice ? <div className="action-notice action-notice--success">{notice}</div> : null}

      {canManageStaffAdmins ? (
        <section className="action-toolbar">
          <button
            className="primary-button toolbar-button"
            type="button"
            onClick={() => setCreateDialogOpen(true)}
            disabled={storesQuery.isLoading || (storesQuery.data?.length ?? 0) === 0}
          >
            <PlusCircle size={17} />
            매장 관리자 추가
          </button>
          <span>{storesQuery.isFetching ? '매장 목록 갱신 중' : `선택 가능 매장 ${storesQuery.data?.length ?? 0}개`}</span>
        </section>
      ) : null}

      <section className="data-panel">
        {query.isLoading ? <div className="loading-block">데이터를 불러오는 중입니다.</div> : null}

        {query.isError ? (
          <div className="error-block">
            <strong>{query.error instanceof Error ? query.error.message : '데이터를 불러오지 못했습니다.'}</strong>
            <button className="secondary-button" type="button" onClick={() => void query.refetch()}>
              <RefreshCcw size={16} />
              다시 시도
            </button>
          </div>
        ) : null}

        {query.data?.length === 0 ? <div className="empty-state">{config.emptyLabel}</div> : null}

        {query.data && query.data.length > 0 ? (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  {config.columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  {canManageStaffAdmins ? <th>작업</th> : null}
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => (
                  <tr key={row.id}>
                    {config.columns.map((column) => (
                      <td key={column.key}>
                        {column.key === 'status' ? (
                          <StatusBadge tone={row.status === 'active' ? 'success' : row.status === 'suspended' ? 'danger' : 'neutral'}>
                            {row.status === 'active' ? '활성' : row.status === 'suspended' ? '정지' : formatCell(row.status)}
                          </StatusBadge>
                        ) : (
                          formatCell(row[column.key])
                        )}
                      </td>
                    ))}
                    {canManageStaffAdmins ? (
                      <td>
                        <div className="row-actions">
                          <button className="secondary-button compact-button" type="button" onClick={() => setPasswordResetTarget(row)}>
                            <KeyRound size={15} />
                            재설정
                          </button>
                          {row.id === profile?.id ? null : row.status === 'suspended' ? (
                            <button className="secondary-button compact-button" type="button" onClick={() => setStatusTarget(row)}>
                              <RotateCcw size={15} />
                              정지 해제
                            </button>
                          ) : (
                            <button className="danger-outline-button compact-button" type="button" onClick={() => setStatusTarget(row)}>
                              <Ban size={15} />
                              정지
                            </button>
                          )}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
      {createDialogOpen ? (
        <ScopedCreateStaffAdminDialog
          stores={storesQuery.data ?? []}
          onClose={() => setCreateDialogOpen(false)}
          onDone={(message) => {
            setCreateDialogOpen(false);
            setNotice(message);
            void query.refetch();
          }}
        />
      ) : null}
      {statusTarget ? (
        <ScopedStoreAdminStatusDialog
          target={statusTarget}
          onClose={() => setStatusTarget(null)}
          onDone={(message) => {
            setStatusTarget(null);
            setNotice(message);
          }}
        />
      ) : null}
      {passwordResetTarget && profile?.id ? (
        <ScopedPasswordResetDialog
          currentAdminId={profile.id}
          target={passwordResetTarget}
          onClose={() => setPasswordResetTarget(null)}
          onDone={(message) => {
            setPasswordResetTarget(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}
